import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import saveSendMessage from 'parog-web/components/chat/utils/save-send-message';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import type { Payload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import MedicalImagingPrescribedJournalEvent from 'parog-web/models/medical-imaging-prescribed-journal-event';
import type AnalyticsService from 'parog-web/services/analytics';
import { sortBy } from 'parog-web/utils/sort';
interface Model extends Payload {
  willSendMessage: boolean;
  chatMessage: string;
}

export default class MedicalImagingEditionFormComponent extends CrudJournalEventBaseComponent {
  static override forcDisplayName =
    "Prescription d'imagerie ou d'examen médical";
  static override fullComponentId = 'prescription/medical-imaging-edition-form';
  static override journalEventClass = MedicalImagingPrescribedJournalEvent;

  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;

  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  async _openPrescriptionInNewTab(): Promise<void> {
    // The last JE created of the given type should be the JE saved in the form.
    const journalEvent = this.store
      .peekAll('medical-imaging-prescribed-journal-event')
      // @ts-expect-error the @types/ember-data types are wrong.
      .toSorted(sortBy('occurredAt', 'desc'))
      .at(-1);

    // Open the PDF prescription in a new tab
    if (journalEvent) {
      this.router.transitionTo(
        'medics-web.protected.patients.patient.prescriptions.medical-imaging-display',
        journalEvent.patient.id,
        journalEvent.id
      );
    } else {
      throw new Error(
        'Impossible to open the PDF prescription because there is no JournalEvent found corresponding to the type : medical-imaging-prescribed-journal-event'
      );
    }
  }

  override afterCompleted = (): Transition =>
    this.router.transitionTo('medics-web.protected.patients.patient.dashboard');

  override async save(): Promise<void> {
    if (!this.isDirty) {
      return;
    }

    const model = await super.save();

    if (this.patient.authorizesAppUsage) {
      await saveSendMessage(model as Model, {
        store: this.store,
        patient: this.args.patient,
        analytics: this.analytics
      });

      await this.store
        .createRecord('patient-chat-message-sent-journal-event', {
          occurredAt: new Date(),
          triggeringJournalEvent: model,
          patient: model.patient,
          body: 'Nouvelle ordonnance reçue'
        })
        .save();
    }

    await this._openPrescriptionInNewTab();
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <h3 class='ui header'>
          Soin en lien avec l'ALD
        </h3>

        <div class='field'>
          <Form.TextArea
            @labelName='Nature du bilan / de l’examen à réaliser'
            @propertyName='typeOfExamAld'
            @rows='1'
          />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='État clinique du patient'
            @propertyName='patientClinicalStateAld'
            @rows='1'
          />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='Indication'
            @propertyName='indicationsAld'
            @rows='1'
          />
        </div>

        <h3 class='ui header'>
          Soin n'étant pas en lien avec l'ALD
        </h3>

        <div class='field'>
          <Form.TextArea
            @labelName='Nature du bilan / de l’examen à réaliser'
            @propertyName='typeOfExamNonAld'
            @rows='1'
          />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='État clinique du patient'
            @propertyName='patientClinicalStateNonAld'
            @rows='1'
          />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='Indication'
            @propertyName='indicationsNonAld'
            @rows='1'
          />
        </div>

        {{#if @patient.authorizesAppUsage}}
          <div class='field'>
            <Form.SendMessage
              @willSendMessage={{true}}
              @defaultChatMessage='Bonjour,
    Je viens de vous éditer une nouvelle ordonnance. Elle est jointe à ce message.
    Bonne journée.'
            />
          </div>
        {{/if}}
      </:content>
    </Provider>
  </template>
}
